import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AuthForm from "@/components/AuthForm";
import AuthLayout from "@/components/layouts/AuthLayout";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { usersApi } from "@/api/users";

type UserData = {
  name?: string;
  email?: string;
};

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, isLoading: authLoading } = useAuth();
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const mode = searchParams.get("mode");
  const isSignup = mode === "signup";
  const verifyEmail = searchParams.get("verify") === "email";
  const provider = searchParams.get("provider");
  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");
  
  // Check if user is already authenticated and redirect accordingly
  useEffect(() => {
    // Skip redirect if we're processing OAuth callback or still loading auth
    if (provider || authLoading || isProcessingAuth) {
      return;
    }

    // If user is authenticated, redirect them away from auth page
    if (user) {
      // Check for redirect parameter
      const redirectTarget = searchParams.get("redirect");
      if (redirectTarget === "onboarding") {
        navigate("/profile?onboarding=true");
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else if (!provider) {
      // Only redirect to login if we're not processing OAuth and no user
      navigate('/login', { replace: true });
    }
  }, [user, provider, authLoading, isProcessingAuth, navigate, searchParams]);

  // Check for OAuth redirect and handle response
  useEffect(() => {
    if (provider) {
      // The user is being redirected back from OAuth provider
      const handleOAuthRedirect = async () => {
        try {
          setIsProcessingAuth(true);
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error("Error getting session after OAuth redirect:", error);
            toast({
              title: "Authentication Error",
              description: error.message || "Could not complete the authentication process.",
              variant: "destructive",
            });
          } else if (data?.session) {
            // Check if user has a profile (to determine if they need onboarding)
            try {
              const userDetails = await usersApi.getMe();

              if (!userDetails.completedOnboarding) {
                // User needs onboarding
                toast({
                  title: userDetails.onboarding_step > 1 ? "Welcome back!" : "Welcome to Wabi Sabi Connect!",
                  description: "Let's set up your profile.",
                });
                navigate("/profile?onboarding=true");
              } else {
                // Existing user with completed onboarding
                toast({
                  title: "Successfully logged in",
                  description: `You've successfully signed in with ${provider}.`,
                });

                // Check for redirect parameter from loginWithProvider
                const redirectTarget = searchParams.get("redirect");
                if (redirectTarget === "onboarding") {
                  navigate("/profile?onboarding=true");
                } else {
                  navigate("/dashboard");
                }
              }
            } catch (userError) {
              // If user profile doesn't exist in backend, needs onboarding
              toast({
                title: "Welcome to Wabi Sabi Connect!",
                description: "Let's set up your profile.",
              });
              navigate("/profile?onboarding=true");
            }
          }
        } catch (err) {
          console.error("Error in OAuth redirect handling:", err);
          toast({
            title: "Authentication Error",
            description: "There was a problem processing your login. Please try again.",
            variant: "destructive",
          });
        } finally {
          setIsProcessingAuth(false);
        }
      };
      
      handleOAuthRedirect();
    }
    
    // Handle error parameters from the OAuth redirect
    if (error) {
      toast({
        title: "Authentication Error",
        description: errorDescription || "There was a problem with the authentication process.",
        variant: "destructive",
      });
    }
  }, [provider, error, errorDescription, navigate, toast]);
  
  // Set initial mode based on URL parameter
  useEffect(() => {
    const authForm = document.querySelector("[data-auth-mode]");
    if (isSignup && authForm) {
      // If mode parameter is signup, switch to signup mode
      const event = new CustomEvent("auth-mode-change", { detail: "signup" });
      document.dispatchEvent(event);
    } else if (verifyEmail && authForm) {
      // If verify email parameter is true, switch to verify email mode
      const event = new CustomEvent("auth-mode-change", { detail: "verify-email" });
      document.dispatchEvent(event);
    }
  }, [isSignup, verifyEmail, searchParams]);

  const handleAuthSuccess = (userData: UserData = {}) => {
    // Redirect newly signed-up users to the profile page with onboarding flag
    // Pass along their name and email if available
    if (isSignup) {
      const searchParams = new URLSearchParams();
      searchParams.append('onboarding', 'true');
      
      // Add user data to the URL if available
      if (userData.name) {
        searchParams.append('name', userData.name);
      }
      if (userData.email) {
        searchParams.append('email', userData.email);
      }
      
      navigate(`/profile?${searchParams.toString()}`);
    } else {
      // Regular login goes to dashboard
      navigate("/dashboard");
    }
  };

  const getPageTitle = () => {
    if (verifyEmail) return "Verify Your Email";
    if (isSignup) return "Start remembering what matters";
    return "Welcome Back";
  };

  // Show loading state while checking if user is already authenticated
  if (authLoading && !provider) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <AuthLayout>
      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-[0_4px_12px_rgba(0,0,0,0.05)]">
          <div className="border-b border-gray-100 p-4 text-center">
            <h1 className="text-xl font-semibold text-gray-900">{getPageTitle()}</h1>
            {isSignup && <p className="text-sm text-gray-600 mt-1">Almost there</p>}
          </div>
          {isProcessingAuth ? (
            <div className="p-6 text-center">
              <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              <p>Processing your authentication...</p>
            </div>
          ) : (
            <AuthForm onSuccess={handleAuthSuccess} />
          )}
        </div>
      </div>
    </AuthLayout>
  );
};

export default Auth;
